/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { MdOutlineLocalShipping } from 'react-icons/md';
import { FormPage } from '../../tailwindComponents/FormPage';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { MultiSelect } from '../../tailwindComponents/MultiSelect';
import { Dropzone } from '../../tailwindComponents/Dropzone';
import { FLEET_CATEGORIES } from '../../constants';

export const FUEL_TYPES = [
  { label: 'Diesel', value: 'Diesel' },
  { label: 'Gas (CNG/LNG)', value: 'Gas (CNG / LNG)' },
  { label: 'Hybrid', value: 'Hybrid' },
  { label: 'Electric', value: 'Electric' },
  { label: 'Hydrogen', value: 'Hydrogen' },
];

export const EMISSION_STANDARDS = [
  { label: 'Euro 1', value: 'Euro1' },
  { label: 'Euro 2', value: 'Euro2' },
  { label: 'Euro 3', value: 'Euro3' },
  { label: 'Euro 4', value: 'Euro4' },
  { label: 'Euro 5', value: 'Euro5' },
  { label: 'Euro 6', value: 'Euro6' },
];

export const VEHICLE_CATEGORIES = [
  { label: 'Curtain Side / Fixed Box', value: 'Curtain Side / Fixed Box' },
  { label: 'Longer semi-trailer (LST)', value: 'Longer semi-trailer (LST)' },
  { label: 'Refrigerated', value: 'Refrigerated' },
  { label: 'Flatbed', value: 'Flatbed' },
  { label: 'Container Trailer', value: 'Container Trailer' },
  { label: 'Draw-bar Trailer', value: 'Draw-bar Trailer' },
  { label: 'Tanker / Silo', value: 'Tanker / Silo' },
  { label: 'Other', value: 'Other' },
];

export const AVERAGE_ANNUAL_MILEAGE_UNITS = [
  { label: 'km', value: 'km' },
  { label: 'miles', value: 'miles' },
];

export const DIMENSIONS_UNITS_OF_MEASURE = [
  { label: 'mm', value: 'mm' },
  { label: 'cm', value: 'cm' },
  { label: 'dm', value: 'dm' },
  { label: 'm', value: 'm' },
];

export const SPECIAL_EQUIPMENT = [
  { label: 'Double deck (fixed)', value: 'Double deck (fixed)' },
  { label: 'Double deck (moving)', value: 'Double deck (moving)' },
  { label: 'High Cube Step deck', value: 'High Cube Step deck' },
  { label: 'Single deck', value: 'Single deck' },
  { label: 'Swap Body', value: 'Swap Body' },
  { label: 'Chilled (2 - 8C)', value: 'Chilled (2 - 8C)' },
  { label: 'Freezer (< -10C)', value: 'Freezer (< -10C)' },
  { label: 'Multi-temperature', value: 'Multi-temperature' },
  { label: 'Tail lift', value: 'Tail lift' },
  { label: 'Morffett', value: 'Morffett' },
  { label: 'Roller bed', value: 'Roller bed' },
  { label: 'Other', value: 'Other' },
];

const blankBecomesNotProvided = (value: number, originalValue: unknown) =>
  originalValue === '' || originalValue === null ? undefined : value;

// Reusable number validation schema
const requiredNumber = (fieldName: string) =>
  Yup.number()
    .transform(blankBecomesNotProvided)
    .required(`${fieldName} is required`)
    .typeError(`${fieldName} must be a number`)
    .min(1, `${fieldName} must be at least 1`);

const optionalNumber = (fieldName: string) =>
  Yup.number()
    .transform(blankBecomesNotProvided)
    .typeError(`${fieldName} must be a number`)
    .min(0, `${fieldName} cannot be negative`);

export function toOptionalNumber(
  value: number | string | null | undefined
): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

// Schema per step - each step only validates its own fields
const schema = [
  // Step 1: General
  Yup.object().shape({
    vehicleType: Yup.string().required('Fleet category is required'),
    model: Yup.string().required('Model is required'),
    registrationNumber: Yup.string().required(
      'Registration number is required'
    ),
  }),
  // Step 2: Specifications
  Yup.object().shape({
    vehicleCategory: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Vehicle category is required'),
    specialEquipment: Yup.array().of(
      Yup.object().shape({
        label: Yup.string(),
        value: Yup.string(),
      })
    ),
    maxPallets: requiredNumber('Max pallets'),
    maxKilograms: requiredNumber('Max weight'),
    width: optionalNumber('Width'),
    height: optionalNumber('Height'),
    length: optionalNumber('Length'),
    dimensionsUnitOfMeasure: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Dimensions unit is required'),
  }),
  // Step 3: Emissions
  Yup.object().shape({
    fuelType: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Fuel type is required'),
    emissionStandard: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Emission standard is required'),
    averageAnnualMileage: optionalNumber('Average annual mileage'),
    averageAnnualMileageUnits: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
      .required('Units are required'),
  }),
];

export type VehicleFormValues = {
  model: string;
  registrationNumber: string;
  vehicleType: string;
  vehicleCategory: { label: string; value: string };
  specialEquipment: { label: string; value: string }[];
  fuelType: { label: string; value: string };
  emissionStandard: { label: string; value: string };
  averageAnnualMileage: number | string;
  averageAnnualMileageUnits: { label: string; value: string };
  maxPallets: number | string;
  maxKilograms: number | string;
  width: number | string;
  height: number | string;
  length: number | string;
  dimensionsUnitOfMeasure: { label: string; value: string };
  vehicleImage: File | null;
};

const defaultFormValues: VehicleFormValues = {
  model: '',
  registrationNumber: '',
  vehicleType: 'VanLessThan1AndHalft',
  vehicleCategory: VEHICLE_CATEGORIES[0],
  specialEquipment: [],
  fuelType: FUEL_TYPES[0],
  emissionStandard: EMISSION_STANDARDS[5],
  averageAnnualMileage: '',
  averageAnnualMileageUnits: AVERAGE_ANNUAL_MILEAGE_UNITS[0],
  maxPallets: '',
  maxKilograms: '',
  width: '',
  height: '',
  length: '',
  dimensionsUnitOfMeasure: DIMENSIONS_UNITS_OF_MEASURE[3],
  vehicleImage: null,
};

type VehicleFormProps = {
  onSubmit: (data: VehicleFormValues) => void;
  isSubmitting: boolean;
  header: string;
  pages: { name: string; href: string; current: boolean }[];
  initialValues?: Partial<VehicleFormValues>;
};

export function VehicleForm({
  onSubmit,
  isSubmitting,
  header,
  pages,
  initialValues,
}: VehicleFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const steps = ['General', 'Specifications', 'Emissions'];

  const methods = useForm<VehicleFormValues>({
    shouldUnregister: false,
    resolver: yupResolver(schema[currentStep - 1]),
    mode: 'onChange',
    defaultValues: { ...defaultFormValues, ...initialValues },
  });

  const { control, watch, setValue, formState } = methods;
  const { errors } = formState;
  const selectedVehicleType = watch('vehicleType');

  // Watch vehicleImage for Dropzone controlled value
  const vehicleImage = useWatch({ control, name: 'vehicleImage' });

  return (
    <FormPage<VehicleFormValues>
      header={header}
      pages={pages}
      methods={methods}
      steps={steps}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    >
      {/* Step 1: General */}
      {currentStep === 1 && (
        <div className='max-w-4xl'>
          <h2 className='text-lg font-semibold text-gray-900 mb-6'>
            Basic Vehicle Information
          </h2>

          {/* Fleet Category Selection */}
          <div className='mb-8'>
            <p className='block text-sm font-medium text-gray-700 mb-3'>
              Select fleet category
            </p>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
              {FLEET_CATEGORIES.map((category) => (
                <button
                  key={category.value}
                  type='button'
                  onClick={() => setValue('vehicleType', category.value)}
                  className={`flex flex-col items-start p-4 rounded-lg border-2 transition-all ${
                    selectedVehicleType === category.value
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className='flex items-center justify-between w-full mb-2'>
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedVehicleType === category.value
                          ? 'border-primary'
                          : 'border-gray-300'
                      }`}
                    >
                      {selectedVehicleType === category.value && (
                        <div className='w-2 h-2 rounded-full bg-primary' />
                      )}
                    </div>
                    <MdOutlineLocalShipping
                      className={`w-6 h-6 ${
                        selectedVehicleType === category.value
                          ? 'text-primary'
                          : 'text-gray-400'
                      }`}
                    />
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      selectedVehicleType === category.value
                        ? 'text-gray-900'
                        : 'text-gray-700'
                    }`}
                  >
                    {category.label}
                  </span>
                  <span className='text-xs text-gray-500'>
                    {category.subtitle}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Vehicle Details */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'>
            <Controller
              name='model'
              control={control}
              render={({ field }) => (
                <Input
                  label='Vehicle make/model'
                  name='model'
                  placeholder='e.g. SCANIA P320'
                  value={field.value}
                  onChange={field.onChange}
                  isInvalid={!!errors.model}
                  validationMessage={errors.model?.message}
                />
              )}
            />
            <Controller
              name='registrationNumber'
              control={control}
              render={({ field }) => (
                <Input
                  label='Registration number'
                  name='registrationNumber'
                  placeholder='e.g. AB12 CDE'
                  value={field.value}
                  onChange={field.onChange}
                  isInvalid={!!errors.registrationNumber}
                  validationMessage={errors.registrationNumber?.message}
                />
              )}
            />
          </div>

          {/* Images Upload */}
          <div className='mb-8'>
            <Dropzone
              dropzoneId='vehicle-image-dropzone'
              label='Images'
              optional
              onDrop={(file) => setValue('vehicleImage', file)}
              fileTypesTagline='JPG, PNG or SVG (max 5 MB)'
              value={vehicleImage instanceof File ? vehicleImage : null}
            />
          </div>
        </div>
      )}

      {/* Step 2: Specifications */}
      {currentStep === 2 && (
        <div className='max-w-4xl'>
          <h2 className='text-lg font-semibold text-gray-900 mb-6'>
            Vehicle Specifications
          </h2>

          {/* Vehicle Category & Equipment */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'>
            <Controller
              name='vehicleCategory'
              control={control}
              render={({ field }) => (
                <Select
                  label='Vehicle category'
                  options={VEHICLE_CATEGORIES}
                  selected={field.value}
                  setSelected={field.onChange}
                  isInvalid={!!errors.vehicleCategory}
                  validationMessage={errors.vehicleCategory?.message}
                />
              )}
            />
            <Controller
              name='specialEquipment'
              control={control}
              render={({ field }) => (
                <MultiSelect
                  label='Special equipment'
                  options={SPECIAL_EQUIPMENT}
                  selected={field.value || []}
                  setSelected={field.onChange}
                  sizeVariant='md'
                />
              )}
            />
          </div>

          {/* Capacity */}
          <h3 className='text-md font-medium text-gray-800 mb-4'>
            Max Capacity
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8'>
            <Controller
              name='maxPallets'
              control={control}
              render={({ field }) => (
                <Input
                  label='Max pallets'
                  name='maxPallets'
                  type='number'
                  value={String(field.value ?? '')}
                  onChange={(e) => field.onChange(e.target.value)}
                  isInvalid={!!errors.maxPallets}
                  validationMessage={errors.maxPallets?.message}
                />
              )}
            />
            <Controller
              name='maxKilograms'
              control={control}
              render={({ field }) => (
                <Input
                  label='Max kilograms'
                  name='maxKilograms'
                  type='number'
                  value={String(field.value ?? '')}
                  onChange={(e) => field.onChange(e.target.value)}
                  isInvalid={!!errors.maxKilograms}
                  validationMessage={errors.maxKilograms?.message}
                />
              )}
            />
          </div>

          {/* Dimensions */}
          <h3 className='text-md font-medium text-gray-800 mb-4'>Dimensions</h3>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-6 mb-8'>
            <Controller
              name='width'
              control={control}
              render={({ field }) => (
                <Input
                  label='Width'
                  name='width'
                  optional
                  type='number'
                  value={String(field.value ?? '')}
                  onChange={(e) => field.onChange(e.target.value)}
                  isInvalid={!!errors.width}
                  validationMessage={errors.width?.message}
                />
              )}
            />
            <Controller
              name='height'
              control={control}
              render={({ field }) => (
                <Input
                  label='Height'
                  name='height'
                  optional
                  type='number'
                  value={String(field.value ?? '')}
                  onChange={(e) => field.onChange(e.target.value)}
                  isInvalid={!!errors.height}
                  validationMessage={errors.height?.message}
                />
              )}
            />
            <Controller
              name='length'
              control={control}
              render={({ field }) => (
                <Input
                  label='Length'
                  name='length'
                  optional
                  type='number'
                  value={String(field.value ?? '')}
                  onChange={(e) => field.onChange(e.target.value)}
                  isInvalid={!!errors.length}
                  validationMessage={errors.length?.message}
                />
              )}
            />
            <Controller
              name='dimensionsUnitOfMeasure'
              control={control}
              render={({ field }) => (
                <Select
                  label='Unit'
                  options={DIMENSIONS_UNITS_OF_MEASURE}
                  selected={field.value}
                  setSelected={field.onChange}
                  isInvalid={!!errors.dimensionsUnitOfMeasure}
                  validationMessage={errors.dimensionsUnitOfMeasure?.message}
                />
              )}
            />
          </div>
        </div>
      )}

      {/* Step 3: Emissions */}
      {currentStep === 3 && (
        <div className='max-w-4xl'>
          <h2 className='text-lg font-semibold text-gray-900 mb-6'>
            Fuel & Emissions
          </h2>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-8'>
            <Controller
              name='fuelType'
              control={control}
              render={({ field }) => (
                <Select
                  label='Fuel type'
                  options={FUEL_TYPES}
                  selected={field.value}
                  setSelected={field.onChange}
                  isInvalid={!!errors.fuelType}
                  validationMessage={errors.fuelType?.message}
                />
              )}
            />
            <Controller
              name='emissionStandard'
              control={control}
              render={({ field }) => (
                <Select
                  label='Emission standard'
                  options={EMISSION_STANDARDS}
                  selected={field.value}
                  setSelected={field.onChange}
                  isInvalid={!!errors.emissionStandard}
                  validationMessage={errors.emissionStandard?.message}
                />
              )}
            />
          </div>

          <h3 className='text-md font-medium text-gray-800 mb-4'>
            Average Annual Mileage
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <Controller
              name='averageAnnualMileage'
              control={control}
              render={({ field }) => (
                <Input
                  label='Average annual mileage'
                  name='averageAnnualMileage'
                  optional
                  type='number'
                  value={String(field.value ?? '')}
                  onChange={(e) => field.onChange(e.target.value)}
                  isInvalid={!!errors.averageAnnualMileage}
                  validationMessage={errors.averageAnnualMileage?.message}
                />
              )}
            />
            <Controller
              name='averageAnnualMileageUnits'
              control={control}
              render={({ field }) => (
                <Select
                  label='Units'
                  options={AVERAGE_ANNUAL_MILEAGE_UNITS}
                  selected={field.value}
                  setSelected={field.onChange}
                  isInvalid={!!errors.averageAnnualMileageUnits}
                  validationMessage={errors.averageAnnualMileageUnits?.message}
                />
              )}
            />
          </div>
        </div>
      )}
    </FormPage>
  );
}
